import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils/queryParams';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import { snakeCase } from 'src/utils/snakeCase/snakeCase';
import { splitSiteOfCareValues } from 'src/components/FilterSets/SiteOfCare/utils';
import type { VolumeByLocationResponse, TopProvider, EducationNetworkItem, DataIssue, ProviderNetworkResponse } from './types';

const SOC_PROFILE_TYPE = 'site-of-care';

const PROFILES_TAG = 'Profiles';
const ENTITY_DETAILS_TAG = 'EntityDetails';
const VOLUME_BY_LOCATION_TAG = 'VolumeByLocation';
const TOP_PROVIDERS_TAG = 'TopProviders';
const EDUCATION_NETWORK_TAG = 'EducationNetwork';
const FRESH_FACES_LOCATIONS_TAG = 'FreshFacesLocations';
const PROVIDER_NETWORK_TAG = 'ProviderNetwork';

// Map for parameter key transformations
const REQ_MAP = {
  years: 'year',
  categories: 'cat',
  codes: 'code',
};

// Map for data types to URL segments — views not in this map have their own dedicated APIs
const dataTypeToUrlMap: Record<string, string> = {
  affiliations: 'affiliations',
  drg: 'drg',
  apc: 'apc',
  icd: 'icd',
  icdp: 'icdp',
  icd10d: 'icd10d',
  cpt: 'cpt',
  hcpcs: 'hcpcs',
  payments: 'payments',
  prescriptions: 'drug',
  referrals: 'inbound-referrals',
  inbound_patient_patterns: 'inbound-referrals',
  outbound_patient_patterns: 'outbound-referrals',
  studies: 'studies',
};

// Views supported by getProfileTabData (includes patient_patterns which is transformed at query time)
export const PROFILE_TAB_DATA_VIEWS = new Set([
  ...Object.keys(dataTypeToUrlMap),
  'patient_patterns',
]);

// Types
interface GetProfileTabDataParams {
  type: string;
  id: string;
  view: string;
  relationship?: 'inbound' | 'outbound';
  filters?: Record<string, any>;
  queryString?: string;
}

interface ReportDataIssueParams {
  providerId: string;
  contentType: string;
  reason: string;
  details: string;
}

interface GetEntityDetailsByIdParams {
  id: string;
}

interface GetVolumeByLocationParams {
  providerId: string;
  queryParams: string;
}

interface GetTopProvidersParams {
  providerId: string;
  queryParams: string;
}

interface GetEducationNetworkParams {
  id: string;
  queryParams: string;
}

export interface FreshFacesLocation {
  provider_id: string;
  name: string | null;
  type: string | null;
  claims: number;
  first_billed_at: string | null;
}

interface GetFreshFacesLocationsParams {
  entityId: string | number;
  strategyId: number;
  lookBackMonths: number;
}

interface GetProviderNetworkParams {
  providerId: string;
  /** Max cards to request. The API clamps to 1–50. */
  pageSize?: number;
}

/**
 * RTK Query API for profile-related endpoints
 */
export const profilesApi = createApi({
  reducerPath: 'profilesApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [
    PROFILES_TAG,
    ENTITY_DETAILS_TAG,
    VOLUME_BY_LOCATION_TAG,
    TOP_PROVIDERS_TAG,
    EDUCATION_NETWORK_TAG,
    FRESH_FACES_LOCATIONS_TAG,
    PROVIDER_NETWORK_TAG,
  ],
  endpoints: (builder) => ({
    /**
     * Query: Get profile tab data with filters
     * Handles complex filtering and query string manipulation for profile views
     */
    getProfileTabData: builder.query<Record<string, any>, GetProfileTabDataParams>({
      query: ({ type, id, view, relationship = 'inbound', filters, queryString = '' }) => {
        // Special case for patient patterns; we need to use the relationship to determine the view
        const relativeView =
          view === 'patient_patterns' ? `${relationship}_${view}` : view;

        // Filter out data type keys from query string
        const newQueryString = queryString
          ?.split('&')
          ?.reduce((acc, curr) => {
            let [key] = curr?.split('=') || [];
            // remove ? if it exists
            if (key?.includes('?')) {
              key = key?.split('?')[1];
            }

            if (!Object.keys(dataTypeToUrlMap).includes(key)) {
              acc += `${curr}&`;
            }

            return acc;
          }, '') || '';

        // Build query params from filters
        const isSocRequest = type === SOC_PROFILE_TYPE;
        let queryParams = `?&${newQueryString}`;
        if (filters) {
          const paramMap = Object.entries(filters).reduce(
            (acc, [k, v]: [string, any]) => {
              // SOC views ignore place_of_service; emit it under any other type only.
              if (isSocRequest && k === 'place_of_service') return acc;

              const selectedValues = Object.keys(v.selectedDict)
                .filter((sk) => v.selectedDict[sk])
                .map((sk) => (isNaN(Number(sk)) ? sk : Number(sk)));
              if (selectedValues.length === 0) return acc;

              // The site_of_care filter holds composite values
              // ("typeId__subtypeId") plus standalone type values. Reuse the
              // shared splitter so this path stays in lockstep with the filter-set /
              // strategy emission: soc_type is the flat type list, soc_subtype keeps
              // the full composite "type__subtype" so the backend scopes each subtype
              // to the exact type it was chosen under (INS-627 per-type scoping).
              // Emitting bare subtypes here would re-introduce cross-type intersection
              // on multi-type selections.
              if (k === 'site_of_care') {
                const { socType, socSubtype } = splitSiteOfCareValues(
                  selectedValues.map((raw) => ({
                    value: String(raw),
                    label: String(raw),
                  }))
                );

                if (socType.length > 0) acc['soc_type'] = socType;
                if (socSubtype.length > 0) acc['soc_subtype'] = socSubtype;

                return acc;
              }

              // Inpatient + Outpatient both selected (or neither) = no filter
              // (All). The backend expects a single-element list, so only send
              // inpatient_flag when exactly one side is selected.
              if (k === 'inpatient_flag') {
                if (selectedValues.length === 1) {
                  acc['inpatient_flag'] = selectedValues;
                }
                return acc;
              }

              // Use REQ_MAP for key transformation unless it's patient_patterns view
              const key = (view !== 'patient_patterns' && REQ_MAP[k]) || k;
              acc[key] = selectedValues;

              return acc;
            },
            {}
          );

          queryParams = getSearchQueryParamsForAPI(paramMap || '');
        }

        // Get the URL segment for this view type
        const urlSegment = dataTypeToUrlMap[relativeView];

        // If the view type is not in the map, it's an invalid request
        if (!urlSegment) {
          throw new Error(`Invalid view type: ${relativeView}`);
        }

        return {
          url: `/v1/${type}/${id}/${urlSegment}${queryParams}`,
          method: 'GET',
        };
      },
      transformResponse: (response: any) => {
        // API returns data wrapped in a "result" object, unwrap it for components
        return response?.result || response;
      },
      providesTags: (result, error, { type, id, view }) => [
        { type: PROFILES_TAG, id: `${type}-${id}-${view}` },
      ],
    }),

    /**
     * Mutation: Report a data issue for a profile
     */
    reportDataIssue: builder.mutation<DataIssue, ReportDataIssueParams>({
      query: ({ providerId, contentType, reason, details }) => ({
        url: `/v1/data-issue`,
        method: 'POST',
        body: {
          provider_id: providerId,
          content_type: snakeCase(contentType),
          reason,
          details,
        },
      }),
      invalidatesTags: [{ type: PROFILES_TAG, id: 'LIST' }],
    }),

    /**
     * Query: Get entity details by ID
     * Fetches detailed information about an entity (provider, organization, etc.)
     */
    getEntityDetailsById: builder.query<Record<string, any>, GetEntityDetailsByIdParams>({
      query: ({ id }) => ({
        url: `v1/entities/entity/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: ENTITY_DETAILS_TAG, id },
      ],
    }),

    /**
     * Query: Get volume by location for a provider
     * Returns procedure volume data grouped by location
     */
    getVolumeByLocation: builder.query<VolumeByLocationResponse, GetVolumeByLocationParams>({
      query: ({ providerId, queryParams }) => ({
        url: `v1/providers/${providerId}/volume-by-location/${queryParams}`,
        method: 'GET',
      }),
      providesTags: (result, error, { providerId }) => [
        { type: VOLUME_BY_LOCATION_TAG, id: providerId },
      ],
    }),

    /**
     * Query: Get top providers at a site
     * Returns top providers based on procedure volume at a location
     */
    getTopProviders: builder.query<MedScout.PaginatedResponse & { results: TopProvider[] }, GetTopProvidersParams>({
      query: ({ providerId, queryParams }) => ({
        url: `v1/providers/${providerId}/top-providers-at-site${queryParams}`,
        method: 'GET',
      }),
      providesTags: (result, error, { providerId }) => [
        { type: TOP_PROVIDERS_TAG, id: providerId },
      ],
    }),

    /**
     * Infinite Query: Get top providers at a site (paginated)
     * Returns top providers with infinite scroll support
     */
    getTopProvidersPaginated: builder.infiniteQuery<
      MedScout.PaginatedResponse & { results: TopProvider[] },
      GetTopProvidersParams,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (!lastPage.next) return undefined;
          return lastPageParam + 1;
        },
      },
      query({ queryArg, pageParam }) {
        const separator = queryArg.queryParams ? '&' : '?';
        return {
          url: `v1/providers/${queryArg.providerId}/top-providers-at-site${queryArg.queryParams}${separator}page=${pageParam}&page_size=25`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { providerId }) => [
        { type: TOP_PROVIDERS_TAG, id: providerId },
      ],
    }),

    /**
     * Query: Get education network for an HCP
     * Returns education-related connections for a healthcare provider
     */
    getEducationNetwork: builder.query<MedScout.PaginatedResponse & { results: EducationNetworkItem[] }, GetEducationNetworkParams>({
      query: ({ id, queryParams }) => ({
        url: `v1/hcp/${id}/education-network${queryParams}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: EDUCATION_NETWORK_TAG, id },
      ],
    }),

    /**
     * Query: Get fresh faces locations for a provider
     * Returns locations where a provider has recently started billing
     */
    getFreshFacesLocations: builder.query<
      FreshFacesLocation[],
      GetFreshFacesLocationsParams
    >({
      query: ({ entityId, strategyId, lookBackMonths }) => {
        const params = buildSearchParamsAuto({
          look_back_months: lookBackMonths,
        });
        return {
          url: `v1/entities/entity/${entityId}/fresh-faces-locations/${strategyId}/?${params.toString()}`,
        };
      },
      providesTags: (result, error, { entityId, strategyId }) => [
        {
          type: FRESH_FACES_LOCATIONS_TAG,
          id: `${entityId}-${strategyId}`,
        },
      ],
    }),

    /**
     * Query: Get the providers connected to an HCP
     * Returns providers who share patients, training, a group or site of care, a
     * society, or industry ties with this HCP, ordered strongest connection first. Powers the
     * Related Providers carousel (INS-635).
     */
    getProviderNetwork: builder.query<
      ProviderNetworkResponse,
      GetProviderNetworkParams
    >({
      query: ({ providerId, pageSize }) => {
        const params = buildSearchParamsAuto({ pageSize });
        return {
          url: `v1/entities/entity/${providerId}/provider-network/?${params.toString()}`,
        };
      },
      providesTags: (result, error, { providerId }) => [
        { type: PROVIDER_NETWORK_TAG, id: providerId },
      ],
    }),
  }),
});

export const {
  useGetProfileTabDataQuery,
  useReportDataIssueMutation,
  useGetEntityDetailsByIdQuery,
  useLazyGetEntityDetailsByIdQuery,
  useGetVolumeByLocationQuery,
  useGetTopProvidersQuery,
  useGetTopProvidersPaginatedInfiniteQuery,
  useGetEducationNetworkQuery,
  useGetFreshFacesLocationsQuery,
  useGetProviderNetworkQuery,
} = profilesApi;
